/**
 * source-adjust-pipeline.ts: G2 per-region source-adjust compositing.
 *
 * Applies the global brightness/contrast pass to `sourceOriginal`, then
 * composites per-region override pixels on top using the `regionLabelPlane`.
 *
 * Pure function; no side effects, no DOM access. Can be tested in happy-dom
 * or pure Node environments.
 */

import { applySourceAdjust } from '@amitone/dither-core';
import type { DemoState, StatePatch } from '../state';

const SOURCE_ADJUST_KEYS = [
  'sourceBrightness',
  'sourceContrast',
  'regionSourceAdjust',
] as const satisfies readonly (keyof StatePatch)[];

const SOURCE_ADJUST_TOPOLOGY_KEYS = [
  'regionEntries',
  'regionStacks',
  'regionLabelPlane',
  'labelMap',
] as const satisfies readonly (keyof StatePatch)[];

function patchHasAny(
  patch: StatePatch,
  keys: readonly (keyof StatePatch)[],
): boolean {
  return keys.some((key) => key in patch);
}

export function shouldRebuildAdjustedSourceForPatch(
  state: DemoState,
  patch: StatePatch,
): boolean {
  if (!state.sourceOriginal || 'source' in patch) return false;

  if (patchHasAny(patch, SOURCE_ADJUST_KEYS)) return true;

  const topologyChanged = patchHasAny(patch, SOURCE_ADJUST_TOPOLOGY_KEYS);
  if (!topologyChanged) return false;

  return (
    state.sourceBrightness !== 0 ||
    state.sourceContrast !== 0 ||
    Object.keys(state.regionSourceAdjust).length > 0
  );
}

/**
 * Build the adjusted source ImageData for the dither pipeline.
 *
 * Steps:
 *   1. Run global `applySourceAdjust(sourceOriginal, { brightness, contrast })`
 *      to produce the base adjusted pixels.
 *   2. For each UUID key in `state.regionSourceAdjust`:
 *      a. Resolve the label(s): region UUID → single label;
 *         stack UUID → all member region labels.
 *      b. Run `applySourceAdjust(sourceOriginal, override)` to get
 *         per-area adjusted pixels.
 *      c. Composite: for each pixel whose `regionLabelPlane` value is in the
 *         label set, replace the global-adjusted pixel with the per-area value.
 *
 * When `sourceOriginal` is null (no image loaded), returns a 1×1 transparent
 * ImageData as a safe sentinel. The caller is responsible for skipping
 * downstream work when no source is available.
 *
 * Performance: O(W×H) per override entry. For typical ≤32 regions this is
 * well under 100 ms on a 2048 px image (same order as the dither pass).
 */
export function buildAdjustedSource(state: DemoState): ImageData {
  const src = state.sourceOriginal;
  if (!src) return new ImageData(1, 1);

  // Step 1: global adjustment pass.
  const adjusted = applySourceAdjust(src, {
    brightness: state.sourceBrightness,
    contrast: state.sourceContrast,
    gamma: 1,
  });

  // Step 2: per-region compositing.
  const overrides = state.regionSourceAdjust;
  const overrideKeys = Object.keys(overrides);
  if (overrideKeys.length === 0 || state.regionLabelPlane.length === 0) {
    return adjusted;
  }

  const { regionEntries, regionStacks, regionLabelPlane } = state;
  const w = src.width;
  const h = src.height;
  const dd = adjusted.data;

  for (const areaKey of overrideKeys) {
    const override = overrides[areaKey];
    if (!override) continue;

    // Collect label values for this area key (region or stack).
    const labels: number[] = [];
    const asRegion = regionEntries.find((e) => e.uuid === areaKey);
    if (asRegion) {
      labels.push(asRegion.label);
    } else {
      const asStack = regionStacks.find((s) => s.uuid === areaKey);
      if (asStack) {
        for (const entry of regionEntries) {
          if (entry.stackId === asStack.uuid) labels.push(entry.label);
        }
      }
    }
    if (labels.length === 0) continue;

    // Run per-area source-adjust from the pristine source.
    const areaAdjusted = applySourceAdjust(src, {
      brightness: override.brightness,
      contrast: override.contrast,
      gamma: 1,
    });
    const ad = areaAdjusted.data;
    const labelSet = new Set(labels);

    // Composite: replace matching pixels in `adjusted` with area-adjusted values.
    for (let i = 0; i < w * h; i++) {
      if (labelSet.has(regionLabelPlane[i]!)) {
        const p = i * 4;
        dd[p]     = ad[p]!;
        dd[p + 1] = ad[p + 1]!;
        dd[p + 2] = ad[p + 2]!;
        // Alpha is unchanged (copied from src in both passes).
      }
    }
  }

  return adjusted;
}
